export type ClientLifecycleScreenId =
  | 'signup'
  | 'demo-activation'
  | 'checkout'
  | 'onboarding'
  | 'awaiting-review'
  | 'paused'
  | 'payment-issue'
  | 'connection-issue'
  | 'offboarded'
  | 'account'
  | 'billing'
  | 'notifications'
  | 'support'
  | 'signout'
  | 'system-states';

export type ClientLifecycleScreenGroup = 'public' | 'lifecycle' | 'profile' | 'review';

export interface ClientLifecycleScreenMeta {
  id: ClientLifecycleScreenId;
  label: string;
  group: ClientLifecycleScreenGroup;
  authenticated: boolean;
}

export const CLIENT_LIFECYCLE_SCREENS: readonly ClientLifecycleScreenMeta[] = [
  { id: 'signup', label: 'Create account', group: 'public', authenticated: false },
  { id: 'demo-activation', label: 'Demo activation', group: 'public', authenticated: true },
  { id: 'checkout', label: 'Activate service', group: 'public', authenticated: true },
  { id: 'onboarding', label: 'Account setup', group: 'lifecycle', authenticated: true },
  { id: 'awaiting-review', label: 'Awaiting ACL review', group: 'lifecycle', authenticated: true },
  { id: 'paused', label: 'Outreach paused', group: 'lifecycle', authenticated: true },
  { id: 'payment-issue', label: 'Payment issue', group: 'lifecycle', authenticated: true },
  { id: 'connection-issue', label: 'Connection issue', group: 'lifecycle', authenticated: true },
  { id: 'offboarded', label: 'Account archive', group: 'lifecycle', authenticated: true },
  { id: 'account', label: 'Account', group: 'profile', authenticated: true },
  { id: 'billing', label: 'Billing', group: 'profile', authenticated: true },
  { id: 'notifications', label: 'Notifications', group: 'profile', authenticated: true },
  { id: 'support', label: 'Support', group: 'profile', authenticated: true },
  { id: 'signout', label: 'Sign out', group: 'profile', authenticated: true },
  { id: 'system-states', label: 'System states', group: 'review', authenticated: true },
] as const;

export const CLIENT_LIFECYCLE_SCREEN_IDS = CLIENT_LIFECYCLE_SCREENS.map((screen) => screen.id);

export interface OnboardingStepMeta {
  id: string;
  label: string;
  shortLabel: string;
  summary: string;
}

export const ONBOARDING_STEPS: readonly OnboardingStepMeta[] = [
  { id: 'identity', label: 'Agent and licensing', shortLabel: 'Identity', summary: 'Agent identity, timezone, and licensed states' },
  { id: 'transfer', label: 'Transfer routing', shortLabel: 'Transfers', summary: 'Destination, availability, and booking fallback' },
  { id: 'calendar', label: 'Calendar availability', shortLabel: 'Calendar', summary: 'Connected calendar and working availability' },
  { id: 'source', label: 'Lead source and consent', shortLabel: 'Lead source', summary: 'Intake access and consent evidence' },
  { id: 'delivery', label: 'Lead delivery', shortLabel: 'Delivery', summary: 'Qualified lead handoff destination' },
  { id: 'business', label: 'Business facts', shortLabel: 'Business', summary: 'Offer facts and qualification priorities' },
  { id: 'review', label: 'Review and submit', shortLabel: 'Review', summary: 'Confirm the buildout request' },
] as const;

export const US_STATES: readonly { code: string; name: string }[] = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'], ['CA', 'California'],
  ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'], ['FL', 'Florida'], ['GA', 'Georgia'],
  ['HI', 'Hawaii'], ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'],
  ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'],
  ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'], ['MS', 'Mississippi'], ['MO', 'Missouri'],
  ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'],
  ['NM', 'New Mexico'], ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'],
  ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'],
  ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'],
  ['VA', 'Virginia'], ['WA', 'Washington'], ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
  ['DC', 'District of Columbia'],
].map(([code, name]) => ({ code, name }));

export const ACCOUNT_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
] as const;

export interface NotificationPreference {
  id: string;
  label: string;
  detail: string;
  required?: boolean;
  defaultEnabled: boolean;
}

export const NOTIFICATION_PREFERENCES: readonly NotificationPreference[] = [
  { id: 'transfer', label: 'Live transfer incoming', detail: 'Immediate alert when a qualified lead is connecting', defaultEnabled: true },
  { id: 'booking', label: 'Appointment booked', detail: 'Confirmation when a lead reserves a time', defaultEnabled: true },
  { id: 'daily', label: 'Daily outcome digest', detail: 'One summary of contacts, bookings, and transfers', defaultEnabled: true },
  { id: 'weekly', label: 'Weekly performance report', detail: 'A Monday account performance summary', defaultEnabled: false },
  { id: 'service', label: 'Service and billing alerts', detail: 'Security, payment, and service interruption notices', required: true, defaultEnabled: true },
] as const;

export const REVIEW_TIMELINE = [
  { id: 'submitted', label: 'Setup submitted', detail: 'Received Jul 9 at 2:18 PM', state: 'complete' },
  { id: 'configure', label: 'ACL configuring your workspace', detail: 'Routing and qualification review in progress', state: 'current' },
  { id: 'test', label: 'Test call and booking', detail: 'Starts after the open items are resolved', state: 'pending' },
  { id: 'ready', label: 'Ready to go live', detail: 'You will receive a launch confirmation', state: 'pending' },
] as const;

export const BILLING_INVOICES = [
  { id: 'INV-1007', date: 'Jul 9, 2026', description: 'AI agent buildout and setup', amount: '$1,997', status: 'Paid' },
  { id: 'INV-1008', date: 'On activation', description: 'Managed AI agent service', amount: '$1,497', status: 'Scheduled' },
] as const;

export const PROFILE_MENU_SCREENS = [
  'account',
  'billing',
  'notifications',
  'support',
  'signout',
] as const;
